export interface LegacyCopyDocument {
  body: { appendChild(node: unknown): unknown };
  createElement(tag: "textarea"): {
    value: string;
    style: { position: string; left: string; top: string; opacity: string };
    setAttribute(name: string, value: string): void;
    select(): void;
    remove(): void;
  };
  execCommand(command: "copy"): boolean;
}

export interface CopyEnvironment {
  clipboard?: { writeText(value: string): Promise<void> };
  document?: LegacyCopyDocument;
}

export async function copyText(text: string, environment?: CopyEnvironment): Promise<boolean> {
  const clipboard =
    environment === undefined
      ? typeof navigator === "undefined"
        ? undefined
        : navigator.clipboard
      : environment.clipboard;
  try {
    if (clipboard?.writeText) {
      await clipboard.writeText(text);
      return true;
    }
  } catch {
    // HTTP deployments and restrictive browser permissions can reject the
    // modern Clipboard API. Fall through to the click-triggered legacy path.
  }

  const documentRef =
    environment === undefined
      ? typeof document === "undefined"
        ? undefined
        : (document as unknown as LegacyCopyDocument)
      : environment.document;
  if (!documentRef?.body) return false;
  const textarea = documentRef.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  textarea.style.opacity = "0";
  documentRef.body.appendChild(textarea);
  try {
    textarea.select();
    return documentRef.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}
